import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, type LucideIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { scaleIn } from "../lib/motion";

export interface MenuItem {
  id: string;
  label: string;
  icon?: LucideIcon;
  onSelect?: () => void;
  href?: string;
  external?: boolean;
  danger?: boolean;
  disabled?: boolean;
  selected?: boolean;
  /** Renders a divider above this item. */
  separated?: boolean;
}

export interface MenuProps {
  trigger: (props: { open: boolean; toggle: () => void }) => ReactNode;
  items: MenuItem[];
  align?: "start" | "end";
  label: string;
  className?: string;
}

/** Lightweight dropdown: click-outside, Escape, roving focus via arrow keys. */
export function Menu({ trigger, items, align = "end", label, className }: MenuProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }

    const updatePosition = () => {
      const triggerElement = containerRef.current;
      const menuElement = menuRef.current;
      if (!triggerElement || !menuElement) return;

      const triggerRect = triggerElement.getBoundingClientRect();
      const menuRect = menuElement.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth;
      const viewportHeight = document.documentElement.clientHeight;
      const margin = 8;
      const gap = 8;

      const preferredLeft =
        align === "end" ? triggerRect.right - menuRect.width : triggerRect.left;
      const left = Math.min(
        Math.max(preferredLeft, margin),
        Math.max(margin, viewportWidth - menuRect.width - margin),
      );
      const spaceBelow = viewportHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      const shouldOpenAbove =
        spaceBelow < menuRect.height + gap && spaceAbove > spaceBelow;
      const preferredTop = shouldOpenAbove
        ? triggerRect.top - menuRect.height - gap
        : triggerRect.bottom + gap;
      const top = Math.min(
        Math.max(preferredTop, margin),
        Math.max(margin, viewportHeight - menuRect.height - margin),
      );

      setPosition({ left, top });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [align, open]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !containerRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {trigger({ open, toggle: () => setOpen((value) => !value) })}

      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {open && (
              <motion.div
                ref={menuRef}
                role="menu"
                aria-label={label}
                variants={scaleIn}
                initial="hidden"
                animate="visible"
                exit="exit"
                style={{
                  left: position?.left ?? 0,
                  top: position?.top ?? 0,
                  transformOrigin: align === "end" ? "top right" : "top left",
                  visibility: position ? "visible" : "hidden",
                }}
                className="fixed z-[100] min-w-52 max-w-[calc(100vw-1rem)] overflow-hidden rounded-xl border border-line bg-surface p-1 shadow-ir-lg"
              >
                {items.map((item) => {
                  const Icon = item.icon;
                  const content = (
                    <>
                      {Icon && <Icon size={15} aria-hidden="true" className="shrink-0" />}
                      <span className="flex-1 truncate text-left">{item.label}</span>
                      {item.selected && (
                        <Check size={14} aria-hidden="true" className="text-gold-600" />
                      )}
                    </>
                  );

                  const itemClass = cn(
                    "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors",
                    "focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-gold-500",
                    item.disabled && "pointer-events-none opacity-45",
                    item.danger
                      ? "text-danger-600 hover:bg-danger-50 dark:text-danger-500 dark:hover:bg-danger-500/10"
                      : "text-content-muted hover:bg-surface-sunken hover:text-content",
                  );

                  return (
                    <div key={item.id}>
                      {item.separated && (
                        <div className="my-1 h-px bg-line" role="separator" />
                      )}
                      {item.href ? (
                        <a
                          role="menuitem"
                          href={item.href}
                          target={item.external ? "_blank" : undefined}
                          rel={item.external ? "noopener noreferrer" : undefined}
                          onClick={() => setOpen(false)}
                          className={itemClass}
                        >
                          {content}
                        </a>
                      ) : (
                        <button
                          role="menuitem"
                          type="button"
                          disabled={item.disabled}
                          onClick={() => {
                            setOpen(false);
                            item.onSelect?.();
                          }}
                          className={itemClass}
                        >
                          {content}
                        </button>
                      )}
                    </div>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </div>
  );
}

/** Standard chevron trigger, for when a custom one is not needed. */
export function MenuButton({
  open,
  toggle,
  children,
  className,
}: {
  open: boolean;
  toggle: () => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={toggle}
      aria-haspopup="menu"
      aria-expanded={open}
      className={cn(
        "inline-flex h-9 items-center gap-1.5 rounded-lg border border-line-strong bg-surface px-3 text-sm",
        "text-content-muted transition-colors hover:border-gold-400 hover:text-content",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500",
        className,
      )}
    >
      {children}
      <ChevronDown
        size={14}
        aria-hidden="true"
        className={cn("transition-transform duration-200", open && "rotate-180")}
      />
    </button>
  );
}
